import { config } from "./config";
import { handleTokenExpiration } from "./authUtils";

const API_BASE_URL = config.apiBaseUrl;
export interface Product {
  SKU: string;
  ASIN: string;
  ProductName: string;
  Category: string;
  Revenue: string;
  Units: number;
  Velocity: string;
  Growth: string;
  Status: string;
  LastSale: string;
  AOV: string;
}
export interface RevenueByMonth {
  month: string;
  revenue: number;
}
export interface RevenueByCategory {
  category: string;
  revenue: number;
  percentage: number;
}
export interface TopPerformer {
  sku: string;
  product: string;
  revenue: string;
  growth: string;
  status: string;
}

export interface MockSalesData {
  totalRevenue: string;
  totalUnits: number;
  avgVelocity: string;
  topCategory: string;
  growthRate: string;
  activeSkus: number;
  products: Product[];
  revenueByMonth: RevenueByMonth[];
  revenueByCategory: RevenueByCategory[];
  topPerformers: TopPerformer[];
}
export interface NewMockSalesData {
  table: string;
  columns: string[];
  data: any[][];
  row_count?: number;
  limit_applied?: number;
  has_more_data?: boolean;
}

export interface Order {
  AmazonOrderId: string;
  PurchaseDate: string;
  LastUpdateDate: string;
  OrderStatus: string;
  FulfillmentChannel: string;
  NumberOfItemsShipped: number;
  NumberOfItemsUnshipped: number;
  PaymentMethod: string;
  PaymentMethodDetails: string;
  MarketplaceId: string;
  ShipmentServiceLevelCategory: string;
  OrderType: string;
  EarliestShipDate: string;
  LatestShipDate: string;
  IsBusinessOrder: boolean;
  IsPrime: boolean;
  IsAccessPointOrder: boolean;
  IsGlobalExpressEnabled: boolean;
  IsPremiumOrder: boolean;
  IsSoldByAB: boolean;
  IsIBA: boolean;
  Asin: string;
  OrderItemId: number;
  SellerSKU: string;
  Title: string;
  QuantityOrdered: number;
  QuantityShipped: number;
  Amount: number;
}
export interface Inventory {
  asin: string;
  fnSku: string;
  sellerSku: string;
  condition: string;
  lastUpdatedTime: string;
  productName: string;
  totalQuantity: number;
  fulfillableQuantity: number;
  inboundWorkingQuantity: number;
  inboundShippedQuantity: number;
  inboundReceivingQuantity: number;
  totalReservedQuantity: number;
  pendingCustomerOrderQuantity: number;
  pendingTransshipmentQuantity: number;
  fcProcessingQuantity: number;
  totalResearchingQuantity: number;
  totalUnfulfillableQuantity: number;
  supplierName: string;
  leadTime: number;
}

export interface MockProductData {
  products: MockProduct[];
}

export interface MockProduct {
  SKU: string;
  Category: string;
  Name: string;
  ASIN: string;
  ["Cost per unit"]: number;
}

interface MarketingResponse {
  catalog: string;
  schema: string;
  table: string;
  columns: string[];
  data: any[];
  row_count?: number;
}

async function getIdToken(): Promise<string> {
  const token = await OfficeRuntime.storage.getItem("token");
  return token;
}

export async function fetchMockProductData(skus?: string[]): Promise<MockProductData> {
  const paramsString = new URLSearchParams();
  if (skus) {
    skus.forEach((sku) => {
      paramsString.append("sku", sku);
    });
  }
  const token = await getIdToken();
  if (!token) {
    throw new Error("Token is not configured. Please login again.");
  }

  const apiUrl = `${API_BASE_URL}/products?` + paramsString.toString();
  console.log("apiUrl", apiUrl);
  console.log("paramsString", paramsString);

  const response = await fetch(apiUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    await handleTokenExpiration("Token expired during product data fetch");
    throw new Error("Token is not configured. Please login again.");
  }

  const data = await response.json();
  return data as MockProductData;
}

export async function fetchMockSalesData(params?: {
  start_purchase_date: string;
  end_purchase_date: string;
  skus: string[];
}): Promise<NewMockSalesData> {
  const paramsString = new URLSearchParams();
  if (params?.start_purchase_date) {
    paramsString.set("start_purchase_date", params.start_purchase_date);
  }
  if (params?.end_purchase_date) {
    paramsString.set("end_purchase_date", params.end_purchase_date);
  }
  if (params?.skus) {
    const skus = params.skus;
    skus.forEach((sku) => {
      paramsString.append("sku", sku);
    });
  }

  const token = await getIdToken();

  if (!token) {
    throw new Error("Token is not configured. Please login again.");
  }

  const apiUrl = `${API_BASE_URL}/sales-data`;
  console.log("apiUrl", apiUrl);
  console.log("paramsString", paramsString);

  try {
    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 401) {
      await handleTokenExpiration("Token expired during sales data fetch");
      throw new Error("Token is not configured. Please login again.");
    }
    if (!response.ok) {
      throw new Error(`Failed to fetch mock sales data: ${response.statusText}`);
    }
    const data = await response.json();

    if (!data.columns || !Array.isArray(data.columns) || !data.data || !Array.isArray(data.data)) {
      throw new Error("Invalid API response: missing columns or data array");
    }

    return {
      table: data.table || "",
      columns: data.columns,
      data: data.data,
      row_count: data.row_count,
      limit_applied: data.limit_applied,
      has_more_data: data.has_more_data,
    } as NewMockSalesData;
  } catch (error) {
    console.error("Error fetching mock sales data:", error);
    throw error;
  }
}

export async function fetchMockInventoryData(): Promise<Inventory[]> {
  console.log("fetchMockInventoryData Inventory Data...");
  const apiUrl = `${API_BASE_URL}/inventory`;
  const token = await getIdToken();
  const response = await fetch(apiUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (response.status === 401) {
    await handleTokenExpiration("Token expired during inventory data fetch");
    throw new Error("Token is not configured. Please login again.");
  }
  const data = await response.json();
  return data.inventory as Inventory[];
}

export async function fetchMockMarketingData(): Promise<MarketingResponse> {
  const limit = 50000;
  const apiUrl = `${config.endpoints.marketingData}?limit=${limit}`;
  const token = await getIdToken();

  if (!token) {
    throw new Error(
      "Token is not configured. Please check your environment variables or .env file."
    );
  }

  const response = await fetch(apiUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (response.status === 401) {
    await handleTokenExpiration("Token expired during marketing data fetch");
    throw new Error("Token is not configured. Please login again.");
  }
  const data = await response.json();
  return data;
}

export async function fetchFilteredAdvertisingData(
  filters: {
    startDate?: string;
    endDate?: string;
    brands?: string[];
    channels?: string[];
    asins?: string[];
    keyword_contains?: string | string[];
    campaign_name?: string | string[];
  }
): Promise<MarketingResponse> {
  let apiUrl = `${config.endpoints.marketingData}/filtered`;

  const apiFilters: any = {};

  if (filters.startDate && filters.endDate) {
    apiFilters.start_date_from = filters.startDate;
    apiFilters.end_date_to = filters.endDate;
  }

  if (filters.brands && filters.brands.length > 0) {
    apiFilters.brand = filters.brands;
  }

  if (filters.channels && filters.channels.length > 0) {
    apiFilters.channels = filters.channels;
  }

  if (filters.asins && filters.asins.length > 0) {
    apiFilters.sku = filters.asins;
  }

  if (filters.keyword_contains) {
    if (Array.isArray(filters.keyword_contains)) {
      const keywords = filters.keyword_contains.filter(
        (k) => typeof k === "string" && k.trim().length > 0
      );
      if (keywords.length > 0) {
        apiFilters.keyword = keywords;
      }
    } else if (typeof filters.keyword_contains === "string" && filters.keyword_contains.trim()) {
      apiFilters.keyword = [filters.keyword_contains.trim()];
    }
  }

  if (filters.campaign_name) {
    if (Array.isArray(filters.campaign_name)) {
      const campaigns = filters.campaign_name.filter(
        (c) => typeof c === "string" && c.trim().length > 0
      );
      if (campaigns.length > 0) {
        apiFilters.campaign_name = campaigns;
      }
    } else if (typeof filters.campaign_name === "string" && filters.campaign_name.trim()) {
      apiFilters.campaign_name = [filters.campaign_name.trim()];
    }
  }

  // Add filters as query parameter
if (Object.keys(apiFilters).length > 0) {
    apiUrl += `?filters=${encodeURIComponent(JSON.stringify(apiFilters))}`;
}

  console.log("🔍 API URL:", apiUrl);
  console.log("📦 Filters sent to backend:", apiFilters);

  const token = await getIdToken();
  if (!token) {
    throw new Error(
      "Token is not configured. Please check your environment variables or .env file."
    );
  }

  const response = await fetch(apiUrl, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  console.log("Advertising data response", response);

  if (response.status === 401) {
    await handleTokenExpiration("Token expired during filtered advertising data fetch");
    throw new Error("Token is not configured. Please login again.");
  }
  if (!response.ok) {
    // Bubble up backend-provided details when available (e.g., 422 row limit)
    let backendMessage: string | undefined;
    try {
      const errorBody = await response.json();
      backendMessage = errorBody?.detail || errorBody?.message;
    } catch (parseError) {
      // ignore JSON parse issues and fall back to status text
    }

    const message =
      backendMessage ||
      `API request failed: ${response.status} ${response.statusText || "Unknown Error"}`;
    throw new Error(message);
  }

  const data = await response.json();
  console.log("✅ API Response:", {
    rowCount: data.row_count,
    columns: data.columns?.length,
    filters: data.filters_applied,
  });

  return data;
}

// Fetch raw advertising data for building distinct filter options (campaigns, keywords, brands, asins)
export async function fetchAdvertisingRawData(limit: number = 50000): Promise<MarketingResponse> {
  const baseUrl = `${API_BASE_URL}/amazon-ads-data`;
  const apiUrl = `${baseUrl}?limit=${encodeURIComponent(String(limit))}`;
  const token = await getIdToken();
  if (!token) {
    throw new Error(
      "Token is not configured. Please check your environment variables or .env file."
    );
  }

  const response = await fetch(apiUrl, {
    method: "GET",
    headers: {
      Accept: "application/json, text/plain",
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    await handleTokenExpiration("Token expired during advertising data fetch");
    throw new Error("Token is not configured. Please login again.");
  }
  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data as MarketingResponse;
}

/**
 * Mock Amazon sales data for the Excel add-in
 */
export const mockSalesData: MockSalesData = {
  totalRevenue: "$1,247,890",
  totalUnits: 5234,
  avgVelocity: "8.7/day",
  topCategory: "Kitchen",
  growthRate: "+18%",
  activeSkus: 127,

  products: [
    {
      SKU: "SPR-KIT-001",
      ASIN: "B08N5WRWNW",
      ProductName: "Professional Kitchen Scale",
      Category: "Kitchen",
      Revenue: "$45,678",
      Units: 234,
      Velocity: "1.2",
      Growth: "+18%",
      Status: "Growing",
      LastSale: "Dec 15",
      AOV: "$195",
    },
    {
      SKU: "SPR-OUT-002",
      ASIN: "B07XYZ123A",
      ProductName: "Outdoor Fire Pit Set",
      Category: "Patio",
      Revenue: "$78,934",
      Units: 67,
      Velocity: "0.35",
      Growth: "-8%",
      Status: "Declining",
      LastSale: "Dec 10",
      AOV: "$1,178",
    },
    {
      SKU: "SPR-HOME-003",
      ASIN: "B09ABC456D",
      ProductName: "Smart Security Camera",
      Category: "Electronics",
      Revenue: "$156,789",
      Units: 445,
      Velocity: "2.8",
      Growth: "+24%",
      Status: "Growing",
      LastSale: "Dec 16",
      AOV: "$352",
    },
    {
      SKU: "SPR-KIT-004",
      ASIN: "B07X8K9M2N",
      ProductName: "Stainless Steel Mixing Bowls Set",
      Category: "Kitchen",
      Revenue: "$8,245",
      Units: 89,
      Velocity: "2.1",
      Growth: "+12%",
      Status: "Growing",
      LastSale: "Dec 14",
      AOV: "$92",
    },
    {
      SKU: "SPR-ELEC-005",
      ASIN: "B08F5Q6R7S",
      ProductName: "Wireless Bluetooth Headphones",
      Category: "Electronics",
      Revenue: "$12,890",
      Units: 156,
      Velocity: "3.4",
      Growth: "+22%",
      Status: "Growing",
      LastSale: "Dec 17",
      AOV: "$82",
    },
    {
      SKU: "SPR-PAT-006",
      ASIN: "B09B2C3D4E",
      ProductName: "Garden Tool Set",
      Category: "Patio",
      Revenue: "$6,780",
      Units: 78,
      Velocity: "1.8",
      Growth: "+7%",
      Status: "Growing",
      LastSale: "Dec 13",
      AOV: "$87",
    },
    {
      SKU: "SPR-KIT-007",
      ASIN: "B07A1B2C3D",
      ProductName: "Digital Meat Thermometer",
      Category: "Kitchen",
      Revenue: "$4,320",
      Units: 234,
      Velocity: "4.2",
      Growth: "+15%",
      Status: "Growing",
      LastSale: "Dec 18",
      AOV: "$18",
    },
    {
      SKU: "SPR-ELEC-008",
      ASIN: "B08G4H5I6J",
      ProductName: "Smart LED Light Bulbs Pack",
      Category: "Electronics",
      Revenue: "$9,876",
      Units: 198,
      Velocity: "3.1",
      Growth: "+9%",
      Status: "Growing",
      LastSale: "Dec 16",
      AOV: "$50",
    },
    {
      SKU: "SPR-PAT-009",
      ASIN: "B09C5D6E7F",
      ProductName: "Patio Umbrella with Stand",
      Category: "Patio",
      Revenue: "$7,890",
      Units: 23,
      Velocity: "0.8",
      Growth: "-5%",
      Status: "Declining",
      LastSale: "Dec 12",
      AOV: "$343",
    },
    {
      SKU: "SPR-KIT-010",
      ASIN: "B07D2E3F4G",
      ProductName: "Silicone Baking Mats Set",
      Category: "Kitchen",
      Revenue: "$3,456",
      Units: 167,
      Velocity: "2.9",
      Growth: "+11%",
      Status: "Growing",
      LastSale: "Dec 15",
      AOV: "$21",
    },
    {
      SKU: "SPR-ELEC-011",
      ASIN: "B08H6I7J8K",
      ProductName: "Portable Bluetooth Speaker",
      Category: "Electronics",
      Revenue: "$11,234",
      Units: 89,
      Velocity: "2.3",
      Growth: "+16%",
      Status: "Growing",
      LastSale: "Dec 17",
      AOV: "$126",
    },
    {
      SKU: "SPR-PAT-012",
      ASIN: "B09D7E8F9G",
      ProductName: "Outdoor String Lights",
      Category: "Patio",
      Revenue: "$5,678",
      Units: 145,
      Velocity: "3.7",
      Growth: "+8%",
      Status: "Growing",
      LastSale: "Dec 14",
      AOV: "$39",
    },
    {
      SKU: "SPR-KIT-013",
      ASIN: "B07E3F4G5H",
      ProductName: "Electric Coffee Grinder",
      Category: "Kitchen",
      Revenue: "$8,901",
      Units: 67,
      Velocity: "1.5",
      Growth: "+6%",
      Status: "Growing",
      LastSale: "Dec 16",
      AOV: "$133",
    },
    {
      SKU: "SPR-ELEC-014",
      ASIN: "B08I7J8K9L",
      ProductName: "Fitness Smartwatch",
      Category: "Electronics",
      Revenue: "$14,567",
      Units: 45,
      Velocity: "1.1",
      Growth: "+25%",
      Status: "Growing",
      LastSale: "Dec 18",
      AOV: "$324",
    },
    {
      SKU: "SPR-PAT-015",
      ASIN: "B09E8F9G0H",
      ProductName: "Hammock with Stand",
      Category: "Patio",
      Revenue: "$6,234",
      Units: 34,
      Velocity: "0.9",
      Growth: "-2%",
      Status: "Declining",
      LastSale: "Dec 11",
      AOV: "$183",
    },
    {
      SKU: "SPR-KIT-016",
      ASIN: "B07F4G5H6I",
      ProductName: "Food Processor",
      Category: "Kitchen",
      Revenue: "$9,876",
      Units: 28,
      Velocity: "0.7",
      Growth: "+3%",
      Status: "Growing",
      LastSale: "Dec 13",
      AOV: "$353",
    },
    {
      SKU: "SPR-ELEC-017",
      ASIN: "B08J8K9L0M",
      ProductName: "Wireless Charging Pad",
      Category: "Electronics",
      Revenue: "$7,890",
      Units: 123,
      Velocity: "2.8",
      Growth: "+14%",
      Status: "Growing",
      LastSale: "Dec 15",
      AOV: "$64",
    },
    {
      SKU: "SPR-PAT-018",
      ASIN: "B09F9G0H1I",
      ProductName: "Outdoor Dining Set",
      Category: "Patio",
      Revenue: "$12,345",
      Units: 12,
      Velocity: "0.4",
      Growth: "-8%",
      Status: "Declining",
      LastSale: "Dec 10",
      AOV: "$1029",
    },
    {
      SKU: "SPR-KIT-019",
      ASIN: "B07G5H6I7J",
      ProductName: "Cast Iron Skillet Set",
      Category: "Kitchen",
      Revenue: "$6,789",
      Units: 56,
      Velocity: "1.3",
      Growth: "+9%",
      Status: "Growing",
      LastSale: "Dec 17",
      AOV: "$121",
    },
    {
      SKU: "SPR-ELEC-020",
      ASIN: "B08K9L0M1N",
      ProductName: "Smart Doorbell Camera",
      Category: "Electronics",
      Revenue: "$13,456",
      Units: 78,
      Velocity: "2.1",
      Growth: "+19%",
      Status: "Growing",
      LastSale: "Dec 16",
      AOV: "$173",
    },
  ],

  revenueByMonth: [
    { month: "Jul", revenue: 180000 },
    { month: "Aug", revenue: 210000 },
    { month: "Sep", revenue: 195000 },
    { month: "Oct", revenue: 240000 },
    { month: "Nov", revenue: 220000 },
    { month: "Dec", revenue: 280000 },
  ],

  revenueByCategory: [
    { category: "Kitchen", revenue: 523000, percentage: 42 },
    { category: "Electronics", revenue: 398000, percentage: 30 },
    { category: "Patio", revenue: 327000, percentage: 28 },
  ],

  topPerformers: [
    {
      sku: "SPR-KIT-001",
      product: "Professional Kitchen Scale",
      revenue: "$5,694",
      growth: "+4%",
      status: "growing",
    },
    {
      sku: "SPR-HOME-003",
      product: "Smart Security Camera",
      revenue: "$10,480",
      growth: "+18%",
      status: "growing",
    },
  ],
};
